import { CurrencyPipe, DatePipe, DecimalPipe, NgClass } from "@angular/common";
import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, SimpleChanges } from "@angular/core";
import { TranslocoService } from "@jsverse/transloco";
import { UntilDestroy } from "@ngneat/until-destroy";
import { IProgressBarComponent } from "@front/m19-ui";

@UntilDestroy()
@Component({
  selector: "app-spend-progress-bar",
  template: `
    <div class="flex min-w-32 flex-col gap-1">
      <IProgressBar [progress]="spend / budget" [color]="_color" [label]="_label ?? ''" />
      <div class="text-xs font-medium whitespace-nowrap" [ngClass]="_color === 'main' ? 'text-main' : 'text-amber-500'">
        {{ _description }}
      </div>
    </div>
  `,
  imports: [IProgressBarComponent, NgClass],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpendProgressBarComponent implements OnChanges {
  private readonly translocoService = inject(TranslocoService);

  @Input({ required: true }) spend!: number;
  @Input({ required: true }) budget!: number;
  @Input() endDate?: Date;
  @Input() currencyCode?: string;
  @Input() locale: string = "en-US";
  @Input() hideCurrency?: boolean;

  _description!: string;
  _label: string | null = null;
  _color: "main" | "warning" = "main";

  ngOnChanges(_changes: SimpleChanges): void {
    const numberPipe = new DecimalPipe(this.locale);
    const currencyPipe = new CurrencyPipe(this.locale);
    const datePipe = new DatePipe(this.locale);

    if (!this.spend || this.spend < this.budget) {
      this._color = "main";
      if (!this.hideCurrency) {
        this._description = `${numberPipe.transform(this.spend ?? 0, "1.0-0")} / ${currencyPipe.transform(
          this.budget,
          this.currencyCode,
          "symbol",
          "1.0-0",
        )}`;

        this._label = datePipe.transform(this.endDate, "mediumDate");
      } else {
        this._label = `${this.spend ?? 0} / ${this.budget}`;
      }
    } else if (this.spend >= this.budget) {
      this._color = "warning";
      this._label = datePipe.transform(this.endDate, "mediumDate");
      this._description = this.translocoService.translate("spend-progress-bar.bids_on_standby", [
        numberPipe.transform(this.spend, "1.0-0"),
        currencyPipe.transform(this.budget, this.currencyCode, "symbol", "1.0-0"),
      ]);
    }
  }
}
